import React, { useState } from 'react';
import { Container, useToast } from '@crisellevaldez/speculo-ui';
import { ArrowRight, Rocket, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TeamContact } from '../components/DesignTeamContact';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';
import { useAutoCreateFlows } from '../hooks/useAutoCreateFlows';
import { LoadingDialog } from '../../../common/dialogs/LoadingDialog';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useClientOwner } from '../../../../hooks/useClientOwner';

interface LastStageGuideProps {
  onComplete?: () => Promise<void> | void;
  organizationName?: string;
  userName?: string;
}

export const LastStageGuide: React.FC<LastStageGuideProps> = ({
  onComplete,
  organizationName = 'your organization',
  userName,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { sendWebhook } = useOnboardingWebhook();
  const { showToast } = useToast();
  const { createAllFlows, areFlowsAlreadyCreated, isCreating, progress } = useAutoCreateFlows();
  const { fetchOrganization } = useOrganizationStore();
  const owner = useClientOwner();

  const handleCreateFlows = async () => {
    setIsLoading(true);
    try {
      // Refresh org to pick up flows_auto_created flag from Stage 3 background creation
      await fetchOrganization(true);

      // Check if flows were already created in background (after Stage 3)
      const flowsExist = areFlowsAlreadyCreated();

      if (flowsExist) {
        console.log('Flows already created, skipping creation step');
      } else {
        // Send webhook notification
        await sendWebhook('Onboarding - Creating Flows', {
          action: 'auto_create_flows',
          organizationName,
          userName,
        });
      }

      // This will either create flows or return early if already created
      const result = await createAllFlows();

      if (!result.success) {
        showToast({
          message: 'Some flows could not be created. Please check the Flows page.',
          variant: 'warning',
        });
      }

      // Mark onboarding as complete (includes reviveTourCompleted: false)
      await onComplete?.();

      // Refresh organization store to ensure latest flags are loaded
      await fetchOrganization(true);

      // Navigate to flows page
      navigate('/flows#revive');
    } catch (error) {
      console.error('Error creating flows:', error);
      showToast({
        message: 'Failed to create flows. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <div className="max-w-4xl mx-auto py-12 px-4">
        {/* Success Header */}
        <div className="text-center mb-12">
          <h1 className="text-2xl xl:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
            🎉 You&apos;re Almost There{userName ? `, ${userName.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Just one more step and your AI assistant{' '}
            <span className="font-semibold text-yellow-500">Remi</span> will be ready to start
            making calls for {organizationName}!
          </p>
        </div>

        {/* Create Your First Flow */}
        <div className="mb-8">
          <button
            onClick={handleCreateFlows}
            disabled={isLoading || isCreating}
            className="w-full flex items-start gap-4 p-6 rounded-xl border-2 border-yellow-400 dark:border-yellow-600/50 bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 hover:border-yellow-500 dark:hover:border-yellow-600 transition-all text-left disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-yellow-400 text-black">
                1
              </div>
              <div className="p-3 rounded-lg bg-yellow-400 text-black">
                {isLoading || isCreating ? (
                  <Loader2 className="w-8 h-8 animate-spin" />
                ) : (
                  <Rocket className="w-8 h-8" />
                )}
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg xl:text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Create Your First Flow! 🚀
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                This is the fun part! We&apos;ll set up your first flow and you can even test a call
                to yourself to hear Remi in action!
              </p>
            </div>
            <ArrowRight className="w-6 h-6 text-yellow-600 self-center" />
          </button>
        </div>

        {/* Team Contact Section */}
        <div className="mb-8">
          <TeamContact
            title="Need Help?"
            description="We're here for you! Our team is ready to help you get the most out of Speculo."
            teamLeadName={owner.displayName}
            teamLeadRole={owner.role}
            teamLeadEmail={owner.email}
            teamLeadImage={owner.imageUrl}
            helperText="Have questions about creating flows or setting up your campaigns? Don't hesitate to reach out!"
          />
        </div>
      </div>

      {/* Loading Dialog for flow creation */}
      <LoadingDialog
        isOpen={isCreating}
        message="Setting up your account... 🎉"
        progress={progress.percentage}
        showWarning={true}
        warningMessage="Please do not close or refresh this page. Leaving will reset the setup process."
      />
    </Container>
  );
};
